import type {
  AtomData,
  ContextValues,
  FindParameters,
  FindResult,
  GetAtomParameters,
} from "@atoms/atom-types";
import { useRef } from "react";
import type { IFrontendTransaction } from "../AtomStore";
import { useCombinedContext } from "../useCombinedContext";
import { useAtomStore } from "./useAtomStore";

type FindFilterDataForAtom<
  AtomDataFromAtomType extends Record<string, AtomData>,
  AtomFindFilterFromAtomType extends {
    [K in keyof AtomDataFromAtomType]?: AtomData;
  },
  TType extends keyof AtomDataFromAtomType,
> = TType extends keyof AtomFindFilterFromAtomType
  ? AtomFindFilterFromAtomType[TType] extends AtomData
    ? AtomFindFilterFromAtomType[TType]
    : AtomDataFromAtomType[TType]
  : AtomDataFromAtomType[TType];

export type TypedFrontendTransaction<
  AtomDataFromAtomType extends Record<string, AtomData>,
  AtomFindFilterFromAtomType extends {
    [K in keyof AtomDataFromAtomType]?: AtomData;
  } = AtomDataFromAtomType,
> = Omit<IFrontendTransaction, "aquireAtomValue" | "findAtoms"> & {
  aquireAtomValue<TType extends keyof AtomDataFromAtomType>(
    atom: TType,
    overrides?: ContextValues,
    parameters?: GetAtomParameters,
  ): Promise<AtomDataFromAtomType[TType]>;
  aquireAtomValue<T extends AtomData>(
    atom: string,
    overrides?: ContextValues,
    parameters?: GetAtomParameters,
  ): Promise<T>;
  findAtoms<
    TType extends keyof AtomDataFromAtomType,
    TParams extends FindParameters<
      AtomDataFromAtomType[TType],
      FindFilterDataForAtom<
        AtomDataFromAtomType,
        AtomFindFilterFromAtomType,
        TType
      >,
      string
    > = FindParameters<
      AtomDataFromAtomType[TType],
      FindFilterDataForAtom<
        AtomDataFromAtomType,
        AtomFindFilterFromAtomType,
        TType
      >,
      string
    >,
  >(
    atomType: TType,
    parameters: TParams,
    overrides?: ContextValues,
  ): Promise<FindResult<AtomDataFromAtomType[TType], TParams> | null>;
};

export function useTransaction(extraContext: ContextValues = {}) {
  const combinedContext = useCombinedContext();
  const store = useAtomStore();
  const transactionRef = useRef<ReturnType<typeof store.getTransaction>>(null);

  if (!transactionRef.current) {
    transactionRef.current = store.getTransaction({
      ...combinedContext,
      ...extraContext,
    });
  }

  return transactionRef.current;
}

export function makeUseTransaction<
  AtomDataFromAtomType extends Record<string, AtomData>,
  AtomFindFilterFromAtomType extends {
    [K in keyof AtomDataFromAtomType]?: AtomData;
  } = AtomDataFromAtomType,
>() {
  return function useTypedTransaction(
    extraContext: ContextValues = {},
  ): TypedFrontendTransaction<
    AtomDataFromAtomType,
    AtomFindFilterFromAtomType
  > {
    return useTransaction(extraContext) as TypedFrontendTransaction<
      AtomDataFromAtomType,
      AtomFindFilterFromAtomType
    >;
  };
}
